import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "./root.store";
import type {
  IPriorityCategoryValue,
  IPriorityCriterion,
  IPriorityCustomField,
  IPriorityGoal,
  IPriorityProgram,
  IRequestPriorityConfig,
  IRequestPrioritizationDraftPayload,
  IRequestPrioritizationPublishPayload,
  TPriorityCriterionKind,
  TPriorityTier,
} from "@/types/request-prioritization";
import { isPermissionError } from "@/lib/reports/is-permission-error";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields } from "@/lib/changed-fields";

/** Deep structural equality via JSON compare — sufficient for the config shape
 *  (plain objects, arrays, primitives; no ordering ambiguity because draft edits
 *  never reorder criteria/rules relative to the baseline). */
function configEquals(a: IRequestPriorityConfig, b: IRequestPriorityConfig): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function cloneConfig(config: IRequestPriorityConfig): IRequestPriorityConfig {
  return JSON.parse(JSON.stringify(config)) as IRequestPriorityConfig;
}

/**
 * Request Prioritization settings store.
 *
 * Unpublished edits are persisted SERVER-SIDE as a draft (#2867): autosave
 * `saveDraft()` PATCHes the draft (saved, not published — no recompute), so edits
 * survive a refresh and are visible to any admin without going live. `baseline`
 * is the last-loaded/saved config the editable `draft` is diffed against;
 * `hasUnpublishedChanges` is the server's truth (a draft exists) that survives a
 * reload even when the local diff is zero. `isDirty` is either — a local edit or
 * a server draft — and gates Publish/Discard. `publish()` PUTs the draft live and
 * adopts the response as the new baseline; `discardChanges()` DELETEs the server
 * draft and adopts the published config. The setter actions mutate `draft` and
 * are the surface the settings page uses.
 */
export class RequestPrioritizationStore {
  /** Last-loaded (or last-saved/published) config. Null until first load. */
  baseline: IRequestPriorityConfig | null = null;
  /** Local editable copy — mutated by the setters; persisted via saveDraft/publish. */
  draft: IRequestPriorityConfig | null = null;
  /** Server truth: an unpublished draft is stored. Survives a reload even when
   *  the local draft/baseline diff is zero, so Publish/Discard stay enabled. */
  hasUnpublishedChanges = false;
  /** ISO timestamp of the last autosave (draft PATCH), or null. Seeds the pill. */
  draftUpdatedAt: string | null = null;
  /** Selectable request-category values for the Request Category criterion. */
  categoryValues: IPriorityCategoryValue[] = [];
  /** Selectable programs for the Linked Program criterion. */
  programs: IPriorityProgram[] = [];
  /** Selectable goals for the Linked Goal criterion. */
  goals: IPriorityGoal[] = [];
  /** Scorable custom fields (add-criteria picker + per-criterion option rows). */
  customFields: IPriorityCustomField[] = [];

  isLoading = false;
  loadError: Error | null = null;
  isSaving = false;

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  /** True when there are unpublished changes to publish/discard — either a
   *  local edit (draft differs from baseline) or a server-side draft that
   *  survived a reload (`hasUnpublishedChanges`). Gates the Publish button and
   *  the Discard affordance. */
  get isDirty(): boolean {
    if (this.hasUnpublishedChanges) return true;
    if (!this.draft || !this.baseline) return false;
    return !configEquals(this.draft, this.baseline);
  }

  /** Load the config + selectable values, seeding both baseline and draft. When
   *  a server draft exists the GET returns the draft overlay, so the page opens
   *  on the pending edits (they survived the refresh). */
  async load(slug: string): Promise<void> {
    this.isLoading = true;
    this.loadError = null;
    try {
      const payload = await this.rootStore.requestPrioritizationService.getConfig(slug);
      runInAction(() => {
        this.baseline = payload.config;
        this.draft = cloneConfig(payload.config);
        this.hasUnpublishedChanges = payload.config.has_unpublished_changes ?? false;
        this.draftUpdatedAt = payload.config.draft_updated_at ?? null;
        this.categoryValues = payload.category_values;
        this.programs = payload.programs;
        // Defaulted, not asserted: the frontend and backend deploy in parallel,
        // so a bundle carrying this can meet an API that predates `goals`.
        this.goals = payload.goals ?? [];
        this.customFields = payload.custom_fields;
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      runInAction(() => {
        this.loadError = error;
      });
      // Same reasoning as request-statuses.store.ts: a 403 here is a role
      // without the settings-read permission this optional priming needs
      // (NG-1879's requester-only Standard role among them) — expected
      // degradation, not a real failure.
      if (isPermissionError(error)) {
        console.warn("[request-prioritization] forbidden for this role", { slug, error });
      } else {
        console.error("[request-prioritization] failed to load config", { slug, error });
      }
      throw error;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  /** The full-config payload the draft/publish endpoints share, built from the
   *  current draft. Publish adds `expected_version`; the draft omits it. */
  private draftPayload(): IRequestPrioritizationDraftPayload | null {
    if (!this.draft) return null;
    return {
      scoring_enabled: this.draft.scoring_enabled,
      urgent_threshold: this.draft.urgent_threshold,
      high_threshold: this.draft.high_threshold,
      medium_threshold: this.draft.medium_threshold,
      low_threshold: this.draft.low_threshold,
      tier_labels: this.draft.tier_labels,
      default_disabled_tier: this.draft.default_disabled_tier,
      criteria: this.draft.criteria,
    };
  }

  /** Adopt a fresh server payload (from publish/discard) as the new baseline +
   *  draft, clearing the unpublished-changes flag (the server draft is gone). */
  private adopt(result: {
    config: IRequestPriorityConfig;
    category_values: IPriorityCategoryValue[];
    programs: IPriorityProgram[];
    goals?: IPriorityGoal[];
    custom_fields: IPriorityCustomField[];
  }): void {
    this.baseline = result.config;
    this.draft = cloneConfig(result.config);
    this.hasUnpublishedChanges = result.config.has_unpublished_changes ?? false;
    this.draftUpdatedAt = result.config.draft_updated_at ?? null;
    this.categoryValues = result.category_values;
    this.programs = result.programs;
    this.goals = result.goals ?? [];
    this.customFields = result.custom_fields;
  }

  /** Autosave the current draft server-side WITHOUT publishing (#2867). Saved,
   *  not applied — no recompute.
   *
   *  Crucially this does NOT overwrite `draft` with the server response: `draft`
   *  is the local source of truth the autosave hook watches (by serialized
   *  value), and the server echoes a fresh `draft_updated_at` every save, so
   *  adopting the response would change the watched value and retrigger autosave
   *  in an endless loop. Instead we snapshot the just-saved draft into `baseline`
   *  (so the local diff resets to clean) and record the server's flag/timestamp
   *  separately, leaving `draft` — and thus the watched value — untouched. On
   *  failure `baseline` is left behind the draft so the next edit still autosaves,
   *  and the error rethrows for the caller. */
  async saveDraft(slug: string, source: AnalyticsSource = "page"): Promise<void> {
    const payload = this.draftPayload();
    if (!this.draft || !payload) return;
    // The exact draft being persisted — snapshot it now so a mid-flight edit
    // isn't mistaken for "saved" (that later edit re-dirties and autosaves again).
    const saved = cloneConfig(this.draft);
    // Which parts of the config this save changed (thresholds, tier labels,
    // criteria...), read against the baseline before it moves to `saved`.
    const fields = changedFields(payload, this.baseline ?? undefined);
    this.isSaving = true;
    try {
      const result = await this.rootStore.requestPrioritizationService.saveDraft(slug, payload);
      runInAction(() => {
        // Baseline tracks the saved snapshot (NOT the server echo) so `isDirty`'s
        // local diff is clean while `draft` stays byte-identical to what the hook
        // watched — no value churn, no autosave loop.
        this.baseline = saved;
        this.hasUnpublishedChanges = result.config.has_unpublished_changes ?? true;
        this.draftUpdatedAt = result.config.draft_updated_at ?? this.draftUpdatedAt;
        // Selectable values may have shifted (e.g. an archived-but-referenced
        // entry) — keep them in step, matching load/publish.
        this.categoryValues = result.category_values;
        this.programs = result.programs;
        this.goals = result.goals ?? [];
        this.customFields = result.custom_fields;
      });
      if (fields.length > 0) track("request_prioritization_updated", { fields, source });
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error("[request-prioritization] failed to save draft", { slug, error });
      throw error;
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  }

  /** Publish the current draft. On success adopts the response as the new
   *  baseline + draft (the server clears the draft); on failure preserves local
   *  edits and rethrows so the caller (page) can surface inline / toast errors.
   *  No `expected_version` — publish always wins (#2867 product decision). */
  async publish(slug: string, source: AnalyticsSource = "page"): Promise<void> {
    const draftPayload = this.draftPayload();
    if (!draftPayload) return;
    const payload: IRequestPrioritizationPublishPayload = draftPayload;
    this.isSaving = true;
    try {
      const result = await this.rootStore.requestPrioritizationService.publishConfig(slug, payload);
      runInAction(() => this.adopt(result));
      track("request_prioritization_published", {
        criteria_count: result.config.criteria.length,
        scoring_enabled: result.config.scoring_enabled,
        source,
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error("[request-prioritization] failed to publish config", { slug, error });
      throw error;
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  }

  /** Discard unpublished changes: DELETE the server draft and adopt the returned
   *  published config (#2867). On failure preserves local edits and rethrows so
   *  the caller can surface a toast. */
  async discardChanges(slug: string, source: AnalyticsSource = "page"): Promise<void> {
    this.isSaving = true;
    try {
      const result = await this.rootStore.requestPrioritizationService.discardDraft(slug);
      runInAction(() => this.adopt(result));
      track("request_prioritization_discarded", { source });
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error("[request-prioritization] failed to discard draft", { slug, error });
      throw error;
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  }

  // --- Instant scoring toggle (#2636) -------------------------------------------

  /** Enable/disable scoring immediately — its own action, NOT part of the draft
   *  publish (#2636). Persists via the dedicated endpoint, then adopts the new
   *  ``scoring_enabled`` + version into BOTH baseline and draft so the switch
   *  reflects server truth and the change isn't counted as a dirty draft edit;
   *  the user's other in-progress draft edits are preserved. On failure the
   *  local state is untouched and the error rethrows for the page to surface.
   *
   *  Disabling triggers the backend freeze (each request keeps its current tier,
   *  default Medium) asynchronously; enabling re-runs auto-compute. */
  async setScoringEnabled(
    slug: string,
    enabled: boolean,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    if (this.baseline?.scoring_enabled === enabled) return;
    this.isSaving = true;
    try {
      const result = await this.rootStore.requestPrioritizationService.setScoringEnabled(
        slug,
        enabled,
      );
      runInAction(() => {
        if (this.baseline) {
          this.baseline.scoring_enabled = result.config.scoring_enabled;
          this.baseline.published_version = result.config.published_version;
        } else {
          this.baseline = result.config;
        }
        if (this.draft) {
          this.draft.scoring_enabled = result.config.scoring_enabled;
          this.draft.published_version = result.config.published_version;
        } else {
          this.draft = cloneConfig(result.config);
        }
        // Adopt the fresh selectable values too (kept in step with load/publish).
        this.categoryValues = result.category_values;
        this.programs = result.programs;
        this.goals = result.goals ?? [];
        this.customFields = result.custom_fields;
      });
      track(
        enabled
          ? "request_prioritization_scoring_enabled"
          : "request_prioritization_scoring_disabled",
        { source },
      );
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error("[request-prioritization] failed to toggle scoring", { slug, enabled, error });
      throw error;
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  }

  // --- Draft mutators (used by the settings page) -------------------------------

  /** Set one tier's inclusive point threshold. Monotonicity is validated on publish. */
  setThreshold(tier: "urgent" | "high" | "medium" | "low", value: number): void {
    if (!this.draft) return;
    this.draft[`${tier}_threshold`] = value;
  }

  /** Set one tier's custom display name (#2635). A blank string restores the
   *  built-in default for that tier. */
  setTierLabel(tier: "urgent" | "high" | "medium" | "low", label: string): void {
    if (!this.draft) return;
    this.draft.tier_labels[tier] = label;
  }

  /** Set the tier every non-overridden request is pinned to while scoring is off
   *  (#2867 item 2). Takes effect on publish. */
  setDefaultDisabledTier(tier: TPriorityTier): void {
    if (!this.draft) return;
    this.draft.default_disabled_tier = tier;
  }

  /** Add a criterion to the draft (no-op if one of the same kind/field already
   *  exists). A newly-added criterion starts with no rules; presence is
   *  enablement — the admin then assigns points. */
  addCriterion(kind: TPriorityCriterionKind, customFieldId?: string): void {
    if (!this.draft) return;
    if (this.findCriterion(kind, customFieldId)) return;
    this.draft.criteria.push({
      kind,
      custom_field_id: kind === "custom_field" ? (customFieldId ?? null) : null,
      rules: [],
    });
  }

  /** Remove a criterion from the draft — its point weights are dropped. The
   *  removal only takes effect on publish (#2637 §4.4). */
  removeCriterion(kind: TPriorityCriterionKind, customFieldId?: string): void {
    if (!this.draft) return;
    this.draft.criteria = this.draft.criteria.filter(
      (c) => !this.criterionMatches(c, kind, customFieldId),
    );
  }

  /**
   * Set the points (0–5) for a single rule inside a criterion, keyed by the rule's
   * match target. Creates the rule if it doesn't exist yet; removes it when set to
   * 0 to keep the payload minimal.
   */
  setRulePoints(
    kind: TPriorityCriterionKind,
    target: { userId?: string; categoryValueId?: string; optionValue?: string },
    points: number,
    customFieldId?: string,
  ): void {
    const criterion = this.findCriterion(kind, customFieldId);
    if (!criterion) return;
    const matches = (rule: IPriorityCriterion["rules"][number]): boolean =>
      (target.userId !== undefined && rule.user_id === target.userId) ||
      (target.categoryValueId !== undefined && rule.category_value_id === target.categoryValueId) ||
      (target.optionValue !== undefined && rule.option_value === target.optionValue);
    const existing = criterion.rules.find(matches);
    if (points <= 0) {
      criterion.rules = criterion.rules.filter((rule) => !matches(rule));
      return;
    }
    if (existing) {
      existing.points = points;
      return;
    }
    criterion.rules.push({
      user_id: target.userId ?? null,
      category_value_id: target.categoryValueId ?? null,
      option_value: target.optionValue ?? null,
      points,
    });
  }

  /** Remove a rule from a criterion (Requester picker's × affordance). */
  removeRule(
    kind: TPriorityCriterionKind,
    target: { userId?: string; categoryValueId?: string; optionValue?: string },
    customFieldId?: string,
  ): void {
    const criterion = this.findCriterion(kind, customFieldId);
    if (!criterion) return;
    criterion.rules = criterion.rules.filter(
      (rule) =>
        !(
          (target.userId !== undefined && rule.user_id === target.userId) ||
          (target.categoryValueId !== undefined &&
            rule.category_value_id === target.categoryValueId) ||
          (target.optionValue !== undefined && rule.option_value === target.optionValue)
        ),
    );
  }

  /** Count of configured (i.e. active) criteria — the panel header summary. */
  get criterionCount(): number {
    return this.draft?.criteria.length ?? 0;
  }

  private criterionMatches(
    criterion: IPriorityCriterion,
    kind: TPriorityCriterionKind,
    customFieldId?: string,
  ): boolean {
    return (
      criterion.kind === kind &&
      (kind !== "custom_field" || criterion.custom_field_id === (customFieldId ?? null))
    );
  }

  private findCriterion(
    kind: TPriorityCriterionKind,
    customFieldId?: string,
  ): IPriorityCriterion | undefined {
    return this.draft?.criteria.find((criterion) =>
      this.criterionMatches(criterion, kind, customFieldId),
    );
  }
}
